import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import type { LLMTabContentForDispatch } from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import type { PositionedItem } from '../CustomDash/components/PageConfigProvider/types/item';

/**
 * Consume-once stash for a PRE-GENERATED custom page layout (resolve-first
 * Phase C, meeting decision 2026-07-08).
 *
 * The `create_custom_page` tool now runs the page-gen stream HEADLESSLY
 * during the turn (drawer shows the streamed thinking) and the Build chip
 * carries the finished layout. On click, the drawer stashes the layout here
 * keyed by the freshly-built draft path and navigates; `NewCustomPage`
 * consumes the stash and seeds the draft config with the generated items —
 * so the user lands on a COMPLETE layout whose widgets simply data-load.
 * No empty draft, no on-page build stream, no resumed turn.
 *
 * Consume-once + TTL: a stale stash (user navigated elsewhere, or reloads
 * the draft path later) must never resurrect an old generation — the draft
 * then falls back to the normal empty-page flow.
 *
 * RELOAD RESTORE: the in-memory stash dies with the page, so reloading a draft
 * URL used to land on an empty "+ Create Chart" canvas even though the chips
 * restored from the query string. A sessionStorage mirror fixes that — consumed
 * at most ONCE per document (`restoreSpent`) and only when the document LOADED
 * on the draft path being mounted (see `initialPathKey`), so the "never
 * resurrect an old generation" invariant above still holds for every in-app
 * navigation — including an in-document "+ New page" onto the same bare draft
 * path after a restore. This covers reload / restored-tab only; a link shared to
 * another user still lands empty (that needs server-side auto-save, deliberately
 * out of scope).
 */

export type PendingGeneratedPage = {
  content: LLMTabContentForDispatch;
  /** Page-level filters the generation resolved (applied via command store). */
  filters?: TalentDiscoveryAiFilterSearchResponseV2;
  prompt: string;
  /**
   * Invoked by the consumer (`CustomDashRoute`) with the MINTED widget items
   * right after it seeds the draft config. Conversion mints fresh item uuids,
   * so the consumer is the only place the ids the widgets mount with exist —
   * the drawer uses them to arm the landing-narration collector (see
   * seeded-build-narration.ts). Fires at most once (consume-once stash);
   * never fires if the stash expires unconsumed.
   */
  onSeeded?: (seededItems: PositionedItem[]) => void;
  at: number;
};

const PENDING_TTL_MS = 60_000;

const pendingByPath = new Map<string, PendingGeneratedPage>();

const RESTORE_KEY_PREFIX = 'ai-draft-restore:';
// Bounds how long a stale generation can linger. The path check below can't
// tell a reloaded AI draft from a reloaded blank one (shared pathname), so a
// tight TTL keeps that residual small.
const RESTORE_TTL_MS = 60 * 60 * 1000;

/**
 * Data only: `onSeeded` is a live drawer callback that can't survive a reload
 * (hence a restore must not replay narration), and `filters` are omitted — the
 * only consumer reads `content` alone, so mirroring them just risks a quota
 * failure on a large config.
 */
type RestorePayload = Pick<PendingGeneratedPage, 'content' | 'prompt'> & {
  at: number;
};

/**
 * Validate before trusting: the mirror can outlive a deploy (up to
 * `RESTORE_TTL_MS`), so a blob from an older build may not match this shape. A
 * bad shape is treated as a cache miss — the draft seeder gets a graceful empty
 * fall-through instead of garbage.
 */
function isRestorePayload(value: unknown): value is RestorePayload {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.content === 'object' &&
    candidate.content !== null &&
    typeof candidate.prompt === 'string' &&
    typeof candidate.at === 'number'
  );
}

/**
 * Key by PATHNAME only: the draft path from `createPageDraft` carries the
 * current search string ("/company?filters=…") while the consumer looks up by
 * `location.pathname` ("/company") — normalizing here keeps both callers
 * agreeing regardless of which form they hold.
 */
function toKey(path: string): string {
  const q = path.indexOf('?');
  return q === -1 ? path : path.slice(0, q);
}

/**
 * The path this document LOADED on. The mirror is consumed only when the draft
 * being mounted IS that path (a genuine reload/restore) AND `restoreSpent` is
 * unset. The path check alone leaks: the key space is tiny (`/company`,
 * `/geography`, `/role`) and `NewCustomPage` has no AI gating, so an in-document
 * "+ New page → Company" after a restore would re-satisfy it and resurrect the
 * stale layout onto a blank draft. The one-shot closes that; a real reload is a
 * fresh document (flag reset), so repeated reloads still restore.
 */
const initialPathKey =
  typeof window === 'undefined' ? null : toKey(window.location.pathname);

// Per-document one-shot, set on ANY successful take (Map hit or restore) so a
// click-through that consumes the in-memory stash also disarms the mirror.
let restoreSpent = false;

export function stashGeneratedPage(
  path: string,
  payload: Omit<PendingGeneratedPage, 'at'>
): void {
  const key = toKey(path);
  const at = Date.now();
  pendingByPath.set(key, { ...payload, at });

  // Best-effort: quota errors or disabled storage must never break the
  // click-through path, which the in-memory Map alone already serves.
  try {
    const mirrored: RestorePayload = {
      content: payload.content,
      prompt: payload.prompt,
      at,
    };
    sessionStorage.setItem(RESTORE_KEY_PREFIX + key, JSON.stringify(mirrored));
  } catch {
    // Reload-restore is a nicety; losing the mirror is not worth surfacing.
  }
}

/** Reads the reload mirror for `key`, dropping it once past its TTL. */
function readRestoreMirror(key: string): PendingGeneratedPage | undefined {
  const storageKey = RESTORE_KEY_PREFIX + key;
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) {
      return undefined;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isRestorePayload(parsed)) {
      // Stale/foreign shape (e.g. written by a pre-deploy build): drop it and
      // fall through to a normal empty draft rather than seeding garbage.
      sessionStorage.removeItem(storageKey);
      return undefined;
    }
    const mirrored = parsed;
    if (Date.now() - mirrored.at > RESTORE_TTL_MS) {
      sessionStorage.removeItem(storageKey);
      return undefined;
    }
    // Deliberately no `onSeeded`: a silent restore must not replay the
    // landing narration the user already heard when the page was built.
    return {
      content: mirrored.content,
      prompt: mirrored.prompt,
      at: mirrored.at,
    };
  } catch {
    return undefined;
  }
}

/** Returns and REMOVES the stash for `path` when present and fresh. */
export function takeGeneratedPage(
  path: string
): PendingGeneratedPage | undefined {
  const key = toKey(path);

  const hit = pendingByPath.get(key);
  if (hit) {
    pendingByPath.delete(key);
    if (Date.now() - hit.at > PENDING_TTL_MS) {
      return undefined;
    }
    // Spend the one-shot so the mirror the same stash wrote can't later
    // resurrect onto an in-document new draft.
    restoreSpent = true;
    return hit;
  }

  // Fall back to the mirror only on a genuine reload of this draft path; the
  // spent flag bars every in-app navigation (including this stash's own
  // click-through) from resurrecting a stale generation.
  if (restoreSpent || key !== initialPathKey) {
    return undefined;
  }
  const restored = readRestoreMirror(key);
  if (restored) {
    restoreSpent = true;
  }
  return restored;
}

/** Test hook. */
function __resetPendingGeneratedPagesForTests(): void {
  pendingByPath.clear();
  restoreSpent = false;
  try {
    const staleKeys: string[] = [];
    for (let index = 0; index < sessionStorage.length; index++) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(RESTORE_KEY_PREFIX)) {
        staleKeys.push(key);
      }
    }
    for (const key of staleKeys) {
      sessionStorage.removeItem(key);
    }
  } catch {
    // No storage in this environment; nothing to clear.
  }
}
